/* Manage the local device keypair lifecycle with optional password-derived recovery. */
import { api } from "./api";
import { generateKeypair } from "./e2e";
import { wrapPrivateKey, unwrapPrivateKey } from "./passwordKey";

const privKeyKey = (uid) => `orbit_pk_${uid}`;

export function getLocalPrivateJwk(uid) {
  const raw = localStorage.getItem(privKeyKey(uid));
  return raw ? JSON.parse(raw) : null;
}

export function setLocalPrivateJwk(uid, jwk) {
  localStorage.setItem(privKeyKey(uid), JSON.stringify(jwk));
}

export function clearLocalPrivateJwk(uid) {
  localStorage.removeItem(privKeyKey(uid));
}

/**
 * Provision a fresh keypair AND wrap the private key with the password.
 * Used at signup. Returns { publicJwk, wrappedPrivateKey, privateJwk }.
 */
export async function provisionKeypairAtSignup(password) {
  const { publicJwk, privateJwk } = await generateKeypair();
  const wrappedPrivateKey = await wrapPrivateKey(privateJwk, password);
  return { publicJwk, wrappedPrivateKey, privateJwk };
}

/**
 * Hydrate keys on login:
 *   - If local private key exists for this user, reuse it.
 *   - Else, if server has a wrapped_private_key AND we have the password, unwrap it.
 *   - Else, regenerate and upload (new device, no password available).
 */
export async function hydrateKeysOnLogin(user, password) {
  if (!user) return null;
  const local = getLocalPrivateJwk(user.id);
  if (local && user.public_key_jwk) {
    return { publicJwk: user.public_key_jwk, privateJwk: local, freshlyGenerated: false };
  }
  // Try unwrap with password
  if (password && user.wrapped_private_key && user.public_key_jwk) {
    try {
      const privateJwk = await unwrapPrivateKey(user.wrapped_private_key, password);
      setLocalPrivateJwk(user.id, privateJwk);
      return { publicJwk: user.public_key_jwk, privateJwk, freshlyGenerated: false, recovered: true };
    } catch (_e) {
      // Wrong password or corrupted; fall through to regenerate
    }
  }
  // Regenerate fresh keypair, wrap with current password if available
  const { publicJwk, privateJwk } = await generateKeypair();
  setLocalPrivateJwk(user.id, privateJwk);
  const payload = { public_key_jwk: publicJwk };
  if (password) {
    payload.wrapped_private_key = await wrapPrivateKey(privateJwk, password);
  }
  await api.patch("/users/me/key", payload);
  return { publicJwk, privateJwk, freshlyGenerated: true };
}

export async function fetchPublicKey(userId) {
  if (!userId) return null;
  try {
    const r = await api.get(`/users/${userId}/public-key`);
    return r.data.public_key_jwk || null;
  } catch {
    return null;
  }
}
